import api from '@/lib/axios';

import type { EffectiveNavigationResponse, NavigationGroupChild } from '@/types/navigation';

// ============================================
// Navigation — effective module config
// ============================================

/**
 * Fetch the effective navigation config for the current user.
 * The backend filters entries by permissions (via X-User-Permissions header
 * injected by the API gateway) and returns only visible groups/items
 * sorted by displayOrder.
 *
 * Response is Map<section, entries[]> keyed by section name
 * (e.g., "sidebar-top", "sidebar-bottom").
 *
 * @param section — optional filter (e.g., "sidebar-top"). Omit for all sections.
 */
export const getEffectiveNavigation = async (
  section?: string,
): Promise<EffectiveNavigationResponse> => {
  const response = await api.get<EffectiveNavigationResponse>(
    '/platform-service/v1/module-config/effective',
    { params: section ? { section } : undefined },
  );
  return response.data;
};

/**
 * Fetch the visible child modules under a single GROUP entry.
 * Used by landing pages (Settings, Documents) to render their tab strip or
 * card grid independently of the boot-time effective response. The backend
 * filters by the caller's permissions (X-User-Permissions header injected
 * by the gateway), so unauthorised modules never appear in the result.
 *
 * @param groupIdentifier — the GROUP entry's identifier
 *   (e.g. 'CATEGORY_CONFIGURATION', 'CATEGORY_EMPLOYEE_DOCUMENTS').
 */
export const getGroupItems = async (
  groupIdentifier: string,
): Promise<NavigationGroupChild[]> => {
  const response = await api.get<NavigationGroupChild[]>(
    `/platform-service/v1/module-config/effective/groups/${groupIdentifier}/items`,
  );
  return response.data;
};
